import React, { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import useHideCookieBanner from '../utils/useHideCookieBanner';
import AdminReadinessBanner from '../components/AdminReadinessBanner';
import AdminSubNav from '../components/AdminSubNav';
import { Navigate } from 'react-router-dom';
import { Search, Loader2, FileText, User, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../utils/apiBase';
import UserResumesModal from '../components/UserResumesModal';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return ''; }
};

function HighlightSnippet({ text, query }) {
  if (!query || !text) return <span>{text}</span>;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase()
          ? <mark key={i} className="bg-[#fbbf24]/40 text-[#0f1115] font-semibold px-0.5">{part}</mark>
          : <span key={i}>{part}</span>
      )}
    </span>
  );
}

function ResultCard({ item, query, onViewUser }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(item.snippet);
      setCopied(true);
      toast.success('Snippet copied');
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error('Copy failed'); }
  }, [item.snippet]);

  return (
    <div
      className="bg-white border border-[#e5e7eb] p-4 hover:border-[#002fa7]/30 transition-colors"
      data-testid={`search-result-${item.resume_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => onViewUser({ user_id: item.user_id, email: item.user_email })}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#002fa7] hover:underline"
              data-testid={`search-result-user-${item.resume_id}`}
            >
              <User size={13} />
              {item.user_email}
            </button>
            {item.user_name && (
              <span className="text-xs text-[#6b7280]">({item.user_name})</span>
            )}
          </div>
          {item.target_job && (
            <p className="text-xs text-[#4b5563] mt-1" data-testid={`search-result-job-${item.resume_id}`}>
              Target: <strong>{item.target_job}</strong>
            </p>
          )}
        </div>
        <button
          onClick={handleCopy}
          className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs border border-[#e5e7eb] hover:bg-[#f4f5f7] transition-colors"
          title="Copy snippet"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
        </button>
      </div>

      <pre
        className="whitespace-pre-wrap break-words text-xs text-[#4b5563] bg-[#f4f5f7] p-3 border border-[#e5e7eb] font-mono leading-relaxed max-h-32 overflow-hidden"
        data-testid={`search-result-snippet-${item.resume_id}`}
      >
        <HighlightSnippet text={item.snippet} query={query} />
      </pre>

      <div className="mt-2 flex items-center justify-between text-[10px] text-[#9ca3af]">
        <span className="font-mono">{item.resume_id}</span>
        <span>
          {item.content_length?.toLocaleString()} chars
          {item.updated_at && <> · updated {fmtDate(item.updated_at)}</>}
        </span>
      </div>
    </div>
  );
}


export default function AdminResumeSearch() {
  useHideCookieBanner();
  const { user, loading: authLoading } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resumeModalUser, setResumeModalUser] = useState(null);
  const inputRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    const trimmed = (q ?? query).trim();
    if (trimmed.length < 2) {
      toast.error('Search query must be at least 2 characters');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/resumes/search`, {
        params: { q: trimmed, limit: 100 },
        withCredentials: true,
      });
      setResults(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Search failed');
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [query]);

  const handleSubmit = (e) => {
    e.preventDefault();
    doSearch();
  };

  if (authLoading) return <div className="pt-24 text-center text-[#6b7280]">Loading...</div>;
  if (!user || user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20 pb-12" data-testid="admin-resume-search-page">
      <AdminReadinessBanner />
      <AdminSubNav />

      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-3 mb-6">
          <FileText size={20} className="text-[#002fa7]" />
          <h1 className="font-satoshi text-xl font-bold text-[#0f1115]">Resume Text Search</h1>
        </div>

        {/* Search bar */}
        <form onSubmit={handleSubmit} className="mb-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9ca3af]" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search inside all resume text... (e.g. Python, AWS, project manager)"
                className="w-full pl-10 pr-4 py-3 border border-[#e5e7eb] text-sm focus:outline-none focus:border-[#002fa7] focus:ring-1 focus:ring-[#002fa7] bg-white"
                data-testid="resume-search-input"
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={loading || query.trim().length < 2}
              className="px-5 py-3 bg-[#002fa7] text-white text-sm font-bold hover:bg-[#001f6f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
              data-testid="resume-search-submit"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              Search
            </button>
          </div>
        </form>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="resume-search-error">
            {error}
          </div>
        )}

        {/* Results */}
        {results && (
          <div>
            <p className="text-sm text-[#6b7280] mb-4" data-testid="resume-search-count">
              <strong className="text-[#0f1115]">{results.count}</strong> resume{results.count !== 1 ? 's' : ''} matching "<strong>{results.query}</strong>"
            </p>

            {results.count === 0 ? (
              <div className="py-12 text-center bg-white border border-[#e5e7eb]" data-testid="resume-search-empty">
                <Search size={28} className="mx-auto text-[#9ca3af] mb-3" />
                <p className="text-sm text-[#6b7280]">No resumes contain "{results.query}"</p>
                <p className="text-xs text-[#9ca3af] mt-1">Try a broader keyword or check spelling</p>
              </div>
            ) : (
              <div className="space-y-3">
                {results.results.map((item) => (
                  <ResultCard
                    key={item.resume_id}
                    item={item}
                    query={results.query}
                    onViewUser={setResumeModalUser}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Empty state before search */}
        {!results && !loading && !error && (
          <div className="py-16 text-center bg-white border border-[#e5e7eb]">
            <Search size={32} className="mx-auto text-[#9ca3af] mb-4" />
            <p className="text-sm text-[#4b5563] font-medium">Search across all user resumes</p>
            <p className="text-xs text-[#9ca3af] mt-1">
              Find resumes mentioning specific skills, companies, or keywords
            </p>
          </div>
        )}
      </div>

      {/* Resume modal */}
      {resumeModalUser && (
        <UserResumesModal user={resumeModalUser} onClose={() => setResumeModalUser(null)} />
      )}
    </div>
  );
}
